// Operation Health — detail view + near-Execute panel model (Step 17G).
//
// Kept as its own tiny, pure module (same reasoning as
// OperationsHub/operationHealthBadge.js): OperationsDetail/index.js
// transitively imports "../FormComponents" -> "../Generic", which cannot be
// imported at all under this project's current Jest setup (pre-existing,
// unrelated: lottie-web's jsdom-incompatible canvas usage, plus a narrowed
// `transformIgnorePatterns` blocking third-party CSS transforms — see the
// Step 17G report). This module returns a plain data model, never JSX; the
// real component (index.js) is the only place that turns it into markup.
import { resolveOperationHealthPresentation, OPERATION_HEALTH_STATUS } from "../../services/operationHealth";

export const OPERATION_HEALTH_PANEL_VISUALS = Object.freeze({
  VALID: { icon: "fa-check-circle", color: "#2e7d32" },
  WARNING: { icon: "fa-triangle-exclamation", color: "#b7791f" },
  BLOCKED: { icon: "fa-ban", color: "#c0392b" },
  STALE: { icon: "fa-rotate", color: "#2980b9" },
  UNKNOWN: { icon: "fa-circle-info", color: "#7f8c8d" },
  NOT_APPLICABLE: { icon: "fa-circle-info", color: "#7f8c8d" },
});

const HIDDEN_PANEL = Object.freeze({ visible: false });

// A NOT_APPLICABLE result whose lifecycle_classification is one of these
// means the operation is a finalized (completed or cancelled) historical
// fact: the detail view already communicates that on its own (execution
// date, results, historical context), so a "verification not applicable" box
// would add a technical detail with no action or data — and could even read
// as "the check failed to run". Hidden entirely, not just the list badge.
// Any other NOT_APPLICABLE (legacy payload without lifecycle_classification,
// a genuinely non-completed operation for which the check is not applicable,
// or an unrecognized future classification) keeps the existing discreet
// panel — only a real backend-persisted historical check snapshot should
// ever replace this box for these three classifications.
const HIDDEN_LIFECYCLE_CLASSIFICATIONS = new Set([
  "COMPLETED_SNAPSHOT",
  "CANCELLED",
  "CANCELLED_HISTORICAL_SNAPSHOT_WITH_COMPLETION_DATE",
]);

/**
 * Builds the Operation Health panel model shown in the operation detail view,
 * next to the Execute action. Never authorizes execution on its own (VALID is
 * presented, not enforced) and never shows a raw payload key.
 *
 * @param {object} operation - the operation row (must carry `operation_health` when the read path attaches it)
 * @param {{isInvalidSrcTank?: boolean}} [legacyInputs] - confined legacy signal, used only when `operation_health` is entirely absent
 */
export function buildOperationHealthPanel(operation, legacyInputs = {}) {
  const presentation = resolveOperationHealthPresentation(operation, legacyInputs);

  // Only hide the panel for the confined legacy fallback when it has nothing
  // to say at all (unmigrated path + no known issue). Every other case
  // (including "unavailable", "not applicable", and any malformed/degraded
  // payload) is shown, per the section 10/11 defensive-and-honest UX rules.
  const isSilentLegacyFallback = presentation.isFallback && !presentation.applicable && !presentation.titleKey;
  if (isSilentLegacyFallback) {
    return HIDDEN_PANEL;
  }

  const isFinalizedLifecycleSnapshot =
    presentation.status === OPERATION_HEALTH_STATUS.NOT_APPLICABLE &&
    HIDDEN_LIFECYCLE_CLASSIFICATIONS.has(presentation.lifecycleClassification);
  if (isFinalizedLifecycleSnapshot) {
    return HIDDEN_PANEL;
  }

  const visualKey = presentation.status || "UNKNOWN";
  const visual = OPERATION_HEALTH_PANEL_VISUALS[visualKey] || OPERATION_HEALTH_PANEL_VISUALS.UNKNOWN;
  const canRefresh =
    presentation.status === OPERATION_HEALTH_STATUS.STALE ||
    presentation.status === OPERATION_HEALTH_STATUS.UNKNOWN;

  return {
    visible: true,
    status: presentation.status,
    icon: visual.icon,
    color: visual.color,
    titleKey: presentation.titleKey,
    descriptionKey: presentation.descriptionKey,
    reasonLabelKeys: presentation.reasonItems.map((item) => ({ key: item.key, labelKey: item.labelKey })),
    canRefresh,
    isBlocking: presentation.blocking,
    isFallback: presentation.isFallback,
  };
}
